import { html, nothing, type TemplateResult } from "lit";
import { customElement, property } from "lit/decorators.js";

import "@phosphor-icons/webcomponents/PhDotsThreeVertical";
import "@phosphor-icons/webcomponents/PhNotePencil";
import "@phosphor-icons/webcomponents/PhExport";
import "@phosphor-icons/webcomponents/PhCopy";
import "@phosphor-icons/webcomponents/PhDownloadSimple";
import "@phosphor-icons/webcomponents/PhTrash";
import "@phosphor-icons/webcomponents/PhEye";
import "@phosphor-icons/webcomponents/PhStop";
import "@phosphor-icons/webcomponents/PhSpinner";
import "@phosphor-icons/webcomponents/PhPlay";
import "@phosphor-icons/webcomponents/PhWarning";
import "ui-components/lucide-icons/chart-column-increasing";
import "@phosphor-icons/webcomponents/PhImage";
import "ui-components/button/wow-button";
import "ui-components/dropdown-menu/dropdown-menu";
import "ui-components/dropdown-menu/dropdown-menu-item";

import { WowSortableTable } from "ui-components/wow-base-table/wow-sortable-table";
import type { LiveEvent } from "api-contracts/rpc/get-live-events";
import type { TableColumn } from "ui-components/wow-base-table/types";
import type { ISortableState, ISortableService } from "ui-components/wow-base-table/sort-types";
import { getAssetUrl } from "helpers/assetHelpers";
import type { LiveEventStatus } from "ui-common/services/live-event-state/types";
import { inject } from "ui-common/di";
import type { ICreatorLiveEventsState, ICreatorLiveEventsService } from "../../services/creator-live-events/types";
import { styles as baseStyles } from "ui-components/wow-base-table/wow-base-table.css";
import { styles } from "./live-events-table.css";
import { t, translate } from "../../translations/i18n";

@customElement("live-events-table")
export class LiveEventsTable extends WowSortableTable<LiveEvent> {
  static override styles = [baseStyles, styles];

  @inject<ICreatorLiveEventsState>("creatorLiveEventsState")
  private liveEventsState!: ICreatorLiveEventsState;

  @inject<ICreatorLiveEventsService>("creatorLiveEventsService")
  private liveEventsService!: ICreatorLiveEventsService;

  @property({ type: Array })
  events: LiveEvent[] = [];

  @property({ type: Number })
  override breakpoint = 1400;

  protected getSortState(): ISortableState<LiveEvent> {
    return this.liveEventsState;
  }

  protected getSortService(): ISortableService {
    return this.liveEventsService;
  }

  protected getItems(): LiveEvent[] {
    return this.events;
  }

  override connectedCallback() {
    super.connectedCallback();
    this.disableRowClick = true;
    this.hoverable = false;
    this.tileRender = (event) => this.renderEventTile(event);
  }

  override willUpdate(changed: Map<string, unknown>) {
    super.willUpdate(changed);
    this.columns = this.buildColumns();
  }

  private buildColumns(): TableColumn<LiveEvent>[] {
    return [
      {
        key: "thumbnail_url",
        header: translate("liveEventsPage.columnCover"),
        render: (_value, row) => this.dimIfEnded(row, this.renderThumbnail(row)),
      },
      {
        key: "title",
        header: translate("liveEventsPage.columnEvent"),
        render: (_value, row) => this.dimIfEnded(row, html`<span>${row.title}</span>`),
      },
      {
        key: "status",
        header: translate("liveEventsPage.columnStatus"),
        sortable: true,
        render: (_value, row) => this.renderLiveEventStatusBadge(row.status),
      },
      {
        key: "scheduled_start_time",
        header: translate("liveEventsPage.columnDate"),
        sortable: true,
        render: (_value, row) =>
          row.scheduled_start_time ? this.dimIfEnded(row, this.renderDate(row.scheduled_start_time)) : "—",
      },
      {
        key: "start_time",
        header: translate("liveEventsPage.columnStartTime"),
        render: (_value, row) =>
          row.scheduled_start_time ? this.dimIfEnded(row, this.renderTime(row.scheduled_start_time)) : "—",
      },
      {
        key: "fee_cents",
        header: translate("liveEventsPage.columnTicketPrice"),
        render: (_value, row) =>
          row.status === "draft" && !row.fee_cents ? "—" : this.dimIfEnded(row, this.renderPrice(row.fee_cents)),
      },
      {
        key: "tickets_sold",
        header: translate("liveEventsPage.columnTicketsSold"),
        render: (_value, row) => this.dimIfEnded(row, String(row.tickets_sold)),
      },
      {
        key: "revenue_cents",
        header: translate("liveEventsPage.columnRevenue"),
        render: (_value, row) => this.dimIfEnded(row, this.renderRevenue(row.revenue_cents)),
      },
      {
        key: "recording_status",
        header: translate("liveEventsPage.columnRecording"),
        render: (_value, row) => this.dimIfEnded(row, this.renderRecordingStatus(row)),
      },
      {
        key: "action",
        header: "",
        render: (_value, row) => this.renderActionButton(row),
      },
      {
        key: "menu",
        header: "",
        render: (_value, row) => this.renderMenu(row),
      },
    ];
  }

  private renderRecordingStatus(event: LiveEvent) {
    const status = event.recording_status;
    if (!status) return html``;

    if (status === "pending" || status === "processing") {
      return html`<ph-spinner size="16" weight="bold"></ph-spinner>`;
    }
    if (status === "completed") {
      return html`<ph-play size="16" weight="fill"></ph-play>`;
    }
    if (status === "failed") {
      return html`<ph-warning size="16" weight="fill"></ph-warning>`;
    }
    return html``;
  }

  private canEndEvent(event: LiveEvent): boolean {
    if (event.status === "live") return true;
    if (event.status === "scheduled" && event.scheduled_start_time) {
      return new Date(event.scheduled_start_time).getTime() <= Date.now();
    }
    return false;
  }

  private dimIfEnded(event: LiveEvent, content: TemplateResult | string): TemplateResult | string {
    if (event.status === "ended" || event.status === "archived") return html`<span class="ended-dim">${content}</span>`;
    return content;
  }

  private renderLiveEventStatusBadge(status: string) {
    const s = status as LiveEventStatus;
    const labelKey: Record<LiveEventStatus, Parameters<typeof t>[0]> = {
      draft: "liveEventsPage.draft",
      scheduled: "liveEventsPage.scheduled",
      live: "liveEventsPage.live",
      ended: "liveEventsPage.ended",
      cancelled: "liveEventsPage.cancelled",
      archived: "liveEventsPage.archived",
    };
    const variant = s === "live" ? "live" : "event";
    return html` <status-badge variant=${variant} ?pulse=${s === "live"}>${t(labelKey[s])}</status-badge> `;
  }

  private renderThumbnail(event: LiveEvent) {
    if (event.thumbnail_url) {
      return html`
        <div class="thumbnail">
          <img src=${getAssetUrl(event.thumbnail_url)} alt=${event.title} />
        </div>
      `;
    }
    return html`
      <div class="thumbnail thumbnail-placeholder">
        <ph-image size="24"></ph-image>
      </div>
    `;
  }

  private renderPrice(feeCents: number | null) {
    if (!feeCents) return translate("liveEventsPage.free");
    return new Intl.NumberFormat(this.locale, {
      style: "currency",
      currency: "EUR",
    }).format(feeCents / 100);
  }

  private renderRevenue(revenueCents: number) {
    return new Intl.NumberFormat(this.locale, {
      style: "currency",
      currency: "EUR",
    }).format(revenueCents / 100);
  }

  private renderContinueEditingButton(event: LiveEvent, size: "small" | "medium" = "small", fullWidth = false) {
    return html`
      <wow-button
        variant="neutral"
        size=${size}
        rounded
        ?full-width=${fullWidth}
        @click=${() => this.emit("edit-event", { event })}
      >
        <ph-note-pencil slot="prefix" size="16"></ph-note-pencil>
        ${t("liveEventsPage.continueEditing")}
      </wow-button>
    `;
  }

  private renderActionButton(event: LiveEvent) {
    if (event.status === "draft") {
      return this.renderContinueEditingButton(event);
    }
    if (event.status === "scheduled" || event.status === "live") {
      return html`
        <wow-button
          variant="neutral"
          size="small"
          rounded
          @click=${() => this.emit("stream-action", { action: "browser", eventId: event.id })}
        >
          ${t("liveEventsPage.startStreaming")}
        </wow-button>
      `;
    }
    if (event.status === "ended") {
      return html`
        <wow-button
          variant="neutral"
          size="small"
          rounded
          @click=${() => this.emit("stream-action", { action: "view-results", eventId: event.id })}
        >
          <lu-chart-column-increasing slot="prefix" size="16"></lu-chart-column-increasing>
          ${t("liveEventsPage.viewResults")}
        </wow-button>
      `;
    }
    return html``;
  }

  private renderMenu(event: LiveEvent) {
    if (event.status === "draft") {
      return html`
        <dropdown-menu
          glass
          placement="bottom-end"
          @dropdown-item-click=${(e: CustomEvent) => this.handleMenuAction(e, event)}
        >
          <wow-button
            slot="trigger"
            variant="subtle"
            size="medium"
            rounded
            .borderColor=${"var(--sds-color-border-default-secondary)"}
            aria-label="More options"
          >
            <ph-dots-three-vertical size="20"></ph-dots-three-vertical>
          </wow-button>
          <dropdown-menu-item action="edit" icon-position="start">
            <ph-note-pencil slot="icon" size="16"></ph-note-pencil>
            ${t("liveEventsPage.editEvent")}
          </dropdown-menu-item>
          <dropdown-menu-item action="delete" variant="danger" icon-position="start">
            <ph-trash slot="icon" size="16"></ph-trash>
            ${t("liveEventsPage.deleteEvent")}
          </dropdown-menu-item>
        </dropdown-menu>
      `;
    }

    return html`
      <dropdown-menu
        glass
        placement="bottom-end"
        @dropdown-item-click=${(e: CustomEvent) => this.handleMenuAction(e, event)}
      >
        <wow-button
          slot="trigger"
          variant="subtle"
          size="medium"
          rounded
          .borderColor=${"var(--sds-color-border-default-secondary)"}
          aria-label="More options"
        >
          <ph-dots-three-vertical size="20"></ph-dots-three-vertical>
        </wow-button>
        <dropdown-menu-item action="edit" icon-position="start">
          <ph-note-pencil slot="icon" size="16"></ph-note-pencil>
          ${t("liveEventsPage.editEvent")}
        </dropdown-menu-item>
        <dropdown-menu-item action="share" icon-position="start">
          <ph-export slot="icon" size="16"></ph-export>
          ${t("liveEventsPage.shareEvent")}
        </dropdown-menu-item>
        <dropdown-menu-item action="duplicate" icon-position="start">
          <ph-copy slot="icon" size="16"></ph-copy>
          ${t("liveEventsPage.duplicateEvent")}
        </dropdown-menu-item>
        <dropdown-menu-item action="watch" icon-position="start">
          <ph-eye slot="icon" size="16"></ph-eye>
          ${t("liveEventsPage.watchEvent")}
        </dropdown-menu-item>
        ${event.status === "ended" && event.recording_status === "completed"
          ? html`
              <dropdown-menu-item action="download-recording" icon-position="start">
                <ph-download-simple slot="icon" size="16"></ph-download-simple>
                ${t("liveEventsPage.downloadRecording")}
              </dropdown-menu-item>
            `
          : nothing}
        ${this.canEndEvent(event)
          ? html`
              <dropdown-menu-item action="end-event" variant="danger" icon-position="start">
                <ph-stop slot="icon" size="16" weight="fill"></ph-stop>
                ${t("liveEventsPage.endEvent")}
              </dropdown-menu-item>
            `
          : nothing}
        ${event.status !== "live" && event.status !== "archived"
          ? event.status === "scheduled" && event.tickets_sold === 0
            ? html`
                <dropdown-menu-item action="delete" variant="danger" icon-position="start">
                  <ph-trash slot="icon" size="16"></ph-trash>
                  ${t("liveEventsPage.deleteEvent")}
                </dropdown-menu-item>
              `
            : html`
                <dropdown-menu-item action="archive" variant="danger" icon-position="start">
                  <ph-trash slot="icon" size="16"></ph-trash>
                  ${t("liveEventsPage.archiveEvent")}
                </dropdown-menu-item>
              `
          : nothing}
      </dropdown-menu>
    `;
  }

  private renderEventTile(event: LiveEvent) {
    return html`
      <div class="event-tile">
        <div class="event-tile-header">
          <div class="event-tile-info">
            <span class="event-tile-title">${event.title}</span>
            ${this.renderLiveEventStatusBadge(event.status)}
          </div>
          ${this.renderThumbnail(event)}
        </div>
        <div class="event-tile-details">
          <span>${event.scheduled_start_time ? this.renderDate(event.scheduled_start_time) : "—"}</span>
          <span>${event.scheduled_start_time ? this.renderTime(event.scheduled_start_time) : "—"}</span>
          <span>${event.status === "draft" && !event.fee_cents ? "—" : this.renderPrice(event.fee_cents)}</span>
          <span>${t("liveEventsPage.columnTicketsSold")}: ${event.tickets_sold}</span>
          <span>${t("liveEventsPage.columnRevenue")}: ${this.renderRevenue(event.revenue_cents)}</span>
        </div>
        <div class="event-tile-actions">${this.renderTileActionButton(event)} ${this.renderMenu(event)}</div>
      </div>
    `;
  }

  private renderTileActionButton(event: LiveEvent) {
    if (event.status === "draft") {
      return this.renderContinueEditingButton(event, "medium", true);
    }
    if (event.status === "scheduled" || event.status === "live") {
      return html`
        <wow-button
          variant="neutral"
          size="medium"
          rounded
          full-width
          @click=${() => this.emit("stream-action", { action: "browser", eventId: event.id })}
        >
          ${t("liveEventsPage.startStreaming")}
        </wow-button>
      `;
    }
    if (event.status === "ended") {
      return html`
        <wow-button
          variant="neutral"
          size="medium"
          rounded
          full-width
          @click=${() => this.emit("stream-action", { action: "view-results", eventId: event.id })}
        >
          <lu-chart-column-increasing slot="prefix" size="16"></lu-chart-column-increasing>
          ${t("liveEventsPage.viewResults")}
        </wow-button>
      `;
    }
    return nothing;
  }

  private handleMenuAction(e: CustomEvent, event: LiveEvent) {
    const action = e.detail?.action;
    switch (action) {
      case "edit":
        this.emit("edit-event", { event });
        break;
      case "share":
        this.emit("share-click", { event });
        break;
      case "duplicate":
        this.emit("duplicate-event", { event });
        break;
      case "watch":
        this.emit("watch-event", { event });
        break;
      case "download-recording":
        this.emit("download-recording", { eventId: event.id });
        break;
      case "end-event":
        this.emit("end-event", { eventId: event.id });
        break;
      case "delete":
        this.emit("delete-event", { eventId: event.id });
        break;
      case "archive":
        this.emit("archive-event", { eventId: event.id });
        break;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "live-events-table": LiveEventsTable;
  }
}
